import { getErrorCause } from './errors.mts'
import { cacheFetch, getOctokit } from './github.mts'

import type { CResult } from '../types.mts'

/**
 * Converts CVE IDs to GHSA IDs using GitHub API.
 * CVE to GHSA mappings are permanent, so we cache for 30 days.
 */
export async function convertCveToGhsa(
  cveId: string,
): Promise<CResult<string>> {
  try {
    const cacheKey = `cve-to-ghsa-${cveId}`
    const octokit = getOctokit()

    const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000

    const response = await cacheFetch(
      cacheKey,
      () =>
        octokit.rest.securityAdvisories.listGlobalAdvisories({
          cve_id: cveId,
          per_page: 1,
        }),
      THIRTY_DAYS_MS,
    )

    if (!response.data.length) {
      return {
        ok: false,
        message: `No GHSA found for CVE ${cveId}`,
      }
    }

    return {
      ok: true,
      data: response.data[0]!.ghsa_id,
    }
  } catch (e) {
    const errorCause = getErrorCause(e)
    const errorLower = errorCause.toLowerCase()
    // Detect GitHub API rate limit and network errors.
    const isRateLimitOrNetworkError =
      errorLower.includes('rate limit') ||
      errorLower.includes('epipe') ||
      errorLower.includes('econnreset') ||
      errorLower.includes('status: 403') ||
      errorLower.includes('status code 403')

    return {
      ok: false,
      message: isRateLimitOrNetworkError
        ? 'GitHub API rate limit exceeded while converting CVE to GHSA. Wait an hour or set SOCKET_CLI_GITHUB_TOKEN environment variable with a personal access token for higher limits.'
        : `Failed to convert CVE to GHSA: ${errorCause}`,
    }
  }
}
